"use client";

// Verbatim port of the private `packages/sdk/react/src/tools/tool-item.tsx`
// (plan 030) — the expandable chrome for `defineToolView`-built views. The
// `TimelineContext` declared below is provided by the block's `ToolTimeline`
// (tool-group.tsx); the private ToolItem also reads it as a fallback so
// SDK-registered views register and paint connectors inside block groups.
import { c as _c } from "react/compiler-runtime";
import { Shimmer } from "@workspace/ui/components/ai-elements/shimmer";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@workspace/ui/components/collapsible";
import { cn } from "@workspace/ui/lib/utils";
import { ChevronRight } from "lucide-react";
import { createContext, type ReactNode, use, useLayoutEffect, useRef } from "react";
import { useToolAutoOpen } from "./use-tool-auto-open";

// ---------------------------------------------------------------------------
// Timeline context — provided by ToolTimeline, consumed by ToolItem
// ---------------------------------------------------------------------------

export type TimelineContextValue = {
  isInTimeline: true;
  isLast: boolean;
  register: () => void;
};
export const TimelineContext = createContext<TimelineContextValue | null>(null);

// ---------------------------------------------------------------------------
// ToolItem
// ---------------------------------------------------------------------------

const ICON_CLASS = "h-3.5 w-3.5 text-muted-foreground";
export { ICON_CLASS as toolIconClass };
export type ToolItemProps = {
  icon: ReactNode;
  loading?: boolean;
  label?: string;
  detail?: string;
  info?: string;
  error?: string;
  expandable?: boolean;
  defaultOpen?: boolean;
  onClick?: () => void;
  children?: ReactNode;
};

/** Fixed-width icon gutter shared by every row + body so columns line up. */
function ToolItemGutter(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="w-5 flex flex-col items-center justify-center shrink-0">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/** Indented body column with the optional timeline connector line. */
function ToolItemBody(t0) {
  const $ = _c(9);
  const {
    showLine,
    children
  } = t0;
  let t1;
  if ($[0] !== showLine) {
    t1 = showLine && <div className="w-px flex-1 bg-border" />;
    $[0] = showLine;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ToolItemGutter>{t1}</ToolItemGutter>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children) {
    t3 = <div className="flex-1 min-w-0 pl-1.5">{children}</div>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex min-w-0">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
type ToolItemTextProps = Pick<ToolItemProps, "loading" | "label" | "detail" | "info" | "error"> & {
  chevron?: boolean;
  isOpen?: boolean;
};

/** The label / detail / status text (or shimmer while loading). */
function ToolItemText(t0) {
  const $ = _c(12);
  const {
    loading,
    label,
    detail,
    info,
    error,
    chevron,
    isOpen
  } = t0;
  let t1;
  if ($[0] !== detail || $[1] !== error || $[2] !== info || $[3] !== label || $[4] !== loading) {
    t1 = loading ? <><Shimmer className="text-xs">{label || "Loading..."}</Shimmer>{detail && <span className="text-xs font-mono truncate min-w-0 text-left">{detail}</span>}</> : <>{label && <span className="text-xs shrink-0">{label}</span>}{detail && <span className="text-xs font-mono truncate min-w-0 text-left">{detail}</span>}{(error || info) && <span className="text-xs opacity-50 truncate">{error || info}</span>}</>;
    $[0] = detail;
    $[1] = error;
    $[2] = info;
    $[3] = label;
    $[4] = loading;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== chevron || $[7] !== isOpen) {
    t2 = chevron && <ChevronRight className={cn("h-3.5 w-3.5 transition-transform shrink-0 self-center", isOpen && "rotate-90")} />;
    $[6] = chevron;
    $[7] = isOpen;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
export function ToolItem({
  icon,
  loading,
  label,
  detail,
  info,
  error,
  expandable = false,
  defaultOpen = false,
  onClick,
  children
}: ToolItemProps) {
  const timeline = use(TimelineContext);
  const inTimeline = !!timeline?.isInTimeline;
  const showLine = inTimeline && !timeline?.isLast;
  const registerRef = useRef(timeline?.register);
  registerRef.current = timeline?.register;
  useLayoutEffect(() => {
    registerRef.current?.();
  }, []);
  const hasExpandableContent = expandable && !!children;
  const {
    isOpen,
    handleOpenChange
  } = useToolAutoOpen(!!loading && !!defaultOpen, {
    defaultOpen
  });
  const text = <ToolItemText chevron={hasExpandableContent} detail={detail} error={error} info={info} isOpen={isOpen} label={label} loading={loading} />;
  if (hasExpandableContent) {
    return <Collapsible className={cn("min-w-0", !inTimeline && "my-2")} onOpenChange={handleOpenChange} open={isOpen}>
        <CollapsibleTrigger className="h-7 flex items-center w-full min-w-0 text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
          <ToolItemGutter>{icon}</ToolItemGutter>
          <div className="flex items-center gap-1.5 min-w-0 pl-1.5">{text}</div>
        </CollapsibleTrigger>
        <ToolItemBody showLine={showLine}>
          <CollapsibleContent>
            <div className="mt-1 rounded-lg border overflow-hidden w-full">
              {children}
            </div>
          </CollapsibleContent>
        </ToolItemBody>
      </Collapsible>;
  }
  const isClickable = !!onClick && !loading;
  return <div className={cn("min-w-0", !inTimeline && "my-2")}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: keyboard-accessible row — has role="button", tabIndex, and onKeyDown */}
      {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: keyboard-accessible row — has role="button", tabIndex, and onKeyDown */}
      <div className={cn("h-7 flex items-center min-w-0 text-muted-foreground", isClickable && "cursor-pointer hover:text-foreground transition-colors")} onClick={isClickable ? onClick : undefined} onKeyDown={isClickable ? e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.();
      }
    } : undefined} role={isClickable ? "button" : undefined} tabIndex={isClickable ? 0 : undefined}>
        <ToolItemGutter>{icon}</ToolItemGutter>
        <div className="flex items-baseline gap-1.5 min-w-0 pl-1.5">{text}</div>
      </div>
      {(showLine || children) && <ToolItemBody showLine={showLine}>
          {children && <div className="w-full overflow-hidden">{children}</div>}
        </ToolItemBody>}
    </div>;
}